import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import RoleCircle from './RoleCircle';
import { PrimaryButton, ScreenShell, SecondaryButton } from './ScreenShared';

export function WelcomeScreen() {
  const [role, setRole] = useState('');

  return (
    <ScreenShell>
      <p className="figma-brand">INSTANT CONNECTIONS</p>
      <h1 className="figma-title">An instant connection,<br />on your terms</h1>
      <p className="figma-sub">Choose how you want to connect</p>

      <div className="figma-role-row">
        <RoleCircle role="host" selected={role === 'host'} onClick={() => setRole('host')} />
        <RoleCircle role="guest" selected={role === 'guest'} onClick={() => setRole('guest')} />
      </div>

      <SecondaryButton to="/how">How this works</SecondaryButton>

      <label className="figma-checkline">
        <input type="checkbox" />
        <span>I have read and agree to <Link to="/terms?onboarding=true">The Terms & Privacy Policy.</Link></span>
      </label>

      <PrimaryButton to="/auth">Continue</PrimaryButton>
    </ScreenShell>
  );
}

export function HowItWorksScreen() {
  return (
    <ScreenShell titleBar={<Link className="figma-toplink" to="/">Close</Link>}>
      <h2 className="figma-section-title">How Instant Connection works</h2>
      <div className="figma-list-card">
        <h3>Choose your role.</h3>
        <p>Host to send invitations. Guest to receive invites. You can switch later.</p>
      </div>
      <div className="figma-list-card">
        <h3>Set or accept a reserve.</h3>
        <p>Guests set their per-minute value. Hosts see the rate before inviting.</p>
      </div>
      <div className="figma-list-card">
        <h3>Start on video.</h3>
        <p>Every conversation starts with a private call, then both choose what is next.</p>
      </div>
      <PrimaryButton to="/">Got it</PrimaryButton>
    </ScreenShell>
  );
}

export function AuthScreen() {
  return (
    <ScreenShell>
      <p className="figma-brand">INSTANT CONNECTIONS</p>
      <h2 className="figma-section-title">Welcome.</h2>
      <p className="figma-sub">New here or coming back?</p>
      <div className="figma-stack">
        <PrimaryButton to="/signup">Create an account</PrimaryButton>
        <SecondaryButton to="/lounge">I already have an account</SecondaryButton>
        <SecondaryButton to="/lounge">Log in</SecondaryButton>
      </div>
    </ScreenShell>
  );
}

export function SignupScreen() {
  return (
    <ScreenShell titleBar={<Link className="figma-toplink" to="/auth">Back</Link>}>
      <h2 className="figma-section-title">Create account</h2>
      <div className="figma-age-gate-card">
        <div className="figma-age-gate-header">
          <div>
            <h3 className="figma-age-gate-title">Age Verification</h3>
            <p className="figma-age-gate-desc">
              Instant Connections is an adult platform. You must be 18 or older to continue.
            </p>
          </div>
        </div>
        <label className="figma-checkline figma-age-checkline">
          <input type="checkbox" defaultChecked />
          <span>I confirm that I am <strong>18 years of age or older</strong> and agree to enter this platform.</span>
        </label>
      </div>
      <div className="figma-form">
        <label>Email<input type="email" placeholder="you@example.com" /></label>
        <label>Password<input type="password" placeholder="Create password" /></label>
        <label>Display name<input type="text" placeholder="Your name" /></label>
      </div>
      <PrimaryButton to="/verify-id">Continue</PrimaryButton>
    </ScreenShell>
  );
}

export function VerifyIdScreen() {
  return (
    <ScreenShell titleBar={<Link className="figma-toplink" to="/signup">Back</Link>}>
      <h2 className="figma-section-title">Let's confirm you're 18</h2>
      <p className="figma-sub">We verify once, then your ID is deleted.</p>
      <div className="figma-list" style={{ marginTop: '12px' }}>
        <div className="figma-list-item" style={{ cursor: 'pointer', borderColor: 'var(--ic-gold)' }}>
          <p className="name">Driver's license</p>
          <span style={{ color: 'var(--ic-gold)' }}>✓</span>
        </div>
        <div className="figma-list-item" style={{ cursor: 'pointer' }}>
          <p className="name">Passport</p>
        </div>
        <div className="figma-list-item" style={{ cursor: 'pointer' }}>
          <p className="name">National ID card</p>
        </div>
      </div>
      <PrimaryButton to="/profile-setup">Verify & continue</PrimaryButton>
    </ScreenShell>
  );
}

export function ProfileSetupScreen() {
  return (
    <ScreenShell>
      <h2 className="figma-section-title">Profile setup</h2>
      <div className="figma-form">
        <label>Age<input type="number" min="18" max="120" placeholder="25" /></label>
        <label>City<input type="text" placeholder="e.g. New York, NY" /></label>
        <label>Bio<textarea placeholder="Tell people about yourself" rows={4} /></label>
        <label>Interests<input type="text" placeholder="Music, travel, sport" /></label>
      </div>
      <PrimaryButton to="/trust-tier">Continue</PrimaryButton>
    </ScreenShell>
  );
}

export function HostTokensScreen() {
  return (
    <ScreenShell>
      <h2 className="figma-section-title">Starting balance</h2>
      <p className="figma-sub">Choose a cash balance to get started.</p>
      <div className="figma-grid-two">
        <div className="figma-token"><strong>$25.00</strong></div>
        <div className="figma-token"><strong>$55.00</strong></div>
      </div>
      <div className="figma-token"><strong>$99.00</strong></div>
      <PrimaryButton to="/youre-in">Continue</PrimaryButton>
    </ScreenShell>
  );
}

export function GuestRateScreen() {
  return (
    <ScreenShell>
      <h2 className="figma-section-title">Set your guest rate</h2>
      <p className="figma-sub">Choose your hourly cash rate.</p>
      <div className="figma-rate">$3.00 / hr</div>
      <input className="figma-range" type="range" min="1" max="10" defaultValue="3" />
      <PrimaryButton to="/youre-in">Continue</PrimaryButton>
    </ScreenShell>
  );
}

export function TrustTierScreen() {
  return (
    <ScreenShell>
      <h2 className="figma-section-title">Trust tier</h2>
      <div className="figma-grid-two">
        <div className="figma-tier">Verified</div>
        <div className="figma-tier">Premium</div>
      </div>
      <div className="figma-tier">Signature</div>
      <PrimaryButton to="/youre-in">Continue</PrimaryButton>
    </ScreenShell>
  );
}

export function YoureInScreen() {
  return (
    <ScreenShell>
      <h2 className="figma-title">You're in.</h2>
      <p className="figma-sub">Your profile is live in tonight's lounge.</p>
      <PrimaryButton to="/lounge">Enter lounge</PrimaryButton>
    </ScreenShell>
  );
}
